import { UploadSource } from "@rms-types/lib/s3/interface";
import { AttachmentPathParams } from "@rms-types/modules/attachment/interface";
import { uniqueId } from "lodash";
import { FileRejection } from "react-dropzone";

import { useUploadStore } from "../zustand/files/useUploadStore";

import { useS3 } from "./useS3";

import { getFilePath } from "@/features/files";

export function useFileUpload() {
  const {
    trackingFiles,
    actions: { setStore, handleFileUploadComplete, handleFileUploadError, handleFileUploadProgress },
  } = useUploadStore();

  const loadingFiles = trackingFiles.filter(({ failed }) => !failed);
  const failedFiles = trackingFiles.filter(({ failed }) => failed);

  const { signFileURLs, uploadFileToS3 } = useS3({
    handleFileUploadProgress,
    handleFileUploadError,
    handleFileUploadComplete,
  });

  const upload = async (
    files: Array<File | FileRejection>,
    uploadSource: UploadSource,
    path?: AttachmentPathParams
  ): Promise<
    {
      name: string;
      url: string;
    }[]
  > => {
    if (!files) return [];

    // Clear any previous store state
    setStore({ trackingFiles: [], uploading: true, uploadSucceeded: false, uploadFailed: false });

    // Remove any necessary hidden files such as system files like .DS_Store
    const filteredFiles = files.filter((file) => (file instanceof File ? !file.name.startsWith(".") : true));

    const trackingFiles = filteredFiles.map((file) => ({
      trackingId: uniqueId(),
      progress: 0,
      failed: !(file instanceof File),
      file: file instanceof File ? file : file.file,
    }));

    // Updated store with tracked files
    setStore({ trackingFiles });

    try {
      // Create signed URLs for every file
      const { files: signedFiles, failed_files } = await signFileURLs(
        trackingFiles.map(({ trackingId, file }) => ({ trackingId, name: file.name })),
        uploadSource,
        {},
        path
      );

      if (failed_files.length)
        failed_files.forEach(({ trackingId, code, message }) =>
          handleFileUploadError(trackingId, code === "S3Error" ? "Failed to upload file" : message)
        );

      // Loop through and upload each file to S3 using their respected signed URL
      const uploadedFiles = await Promise.all(
        signedFiles.map(({ trackingId, uploadLink }) => {
          const matchingTrackingFile = trackingFiles.find((file) => file.trackingId === trackingId);
          return !matchingTrackingFile ? undefined : uploadFileToS3(matchingTrackingFile, uploadLink);
        })
      );

      if (uploadedFiles.length === 0) {
        setStore({ uploadSucceeded: true, uploading: false });
        return [];
      }

      return uploadedFiles
        .flatMap((uploadedFile) => uploadedFile ?? [])
        .map(({ file, url }) => ({ name: getFilePath(file) || file.name, url }));
    } catch (error) {
      //TODO Sentry.captureException(error);
      setStore({ uploadFailed: true, uploading: false });
      return [];
    }
  };

  return {
    upload,
    loadingFiles,
    failedFiles,
  };
}
